/**
 * API Key Input Component
 * Secure input for Google API key with masking and save functionality
 */

import { useState, useId } from "react";
import { cn } from "@utils/cn";
import {
  EyeIcon,
  EyeOffIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  SaveIcon,
  TrashIcon,
  CopyIcon,
  DownloadIcon,
  LockIcon,
} from "./icons";

interface ApiKeyInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
}

/**
 * Validates API key format.
 * Google API keys are typically 39 characters and contain alphanumeric chars plus - and _.
 * We use a lenient check (30+ chars) to avoid breaking if Google changes their format.
 */
function isValidApiKeyFormat(key: string): boolean {
  return key.length >= 30 && /^[A-Za-z0-9_-]+$/.test(key);
}

export function ApiKeyInput({ value, onChange, className }: ApiKeyInputProps) {
  const [inputValue, setInputValue] = useState(value || "");
  const [showKey, setShowKey] = useState(false);
  const [isSaved, setIsSaved] = useState(!!value);
  const [validationState, setValidationState] = useState<
    "idle" | "valid" | "invalid"
  >("idle");
  const [prevValue, setPrevValue] = useState(value);

  // Generate unique IDs for accessibility
  const inputId = useId();

  // Sync with external value changes (using state comparison pattern)
  if (value !== prevValue) {
    setPrevValue(value);
    if (value !== null) {
      setInputValue(value);
      setIsSaved(true);
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim();
    setInputValue(newValue);
    setIsSaved(false);

    // Validate format
    if (newValue === "") {
      setValidationState("idle");
    } else if (isValidApiKeyFormat(newValue)) {
      setValidationState("valid");
    } else {
      setValidationState("invalid");
    }
  };

  const handleSave = () => {
    if (inputValue && isValidApiKeyFormat(inputValue)) {
      onChange(inputValue);
      setIsSaved(true);
    }
  };

  const handleClear = () => {
    setInputValue("");
    onChange(null);
    setIsSaved(false);
    setValidationState("idle");
  };

  const generateEnvContent = () => {
    return `GOOGLE_GENAI_USE_VERTEXAI=FALSE\nGOOGLE_API_KEY=${inputValue}`;
  };

  const handleCopyEnv = async () => {
    try {
      await navigator.clipboard.writeText(generateEnvContent());
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownloadEnv = () => {
    const content = generateEnvContent();
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = ".env";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Input field */}
      <div className="space-y-2">
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-900 dark:text-white"
        >
          Your Google API Key
        </label>
        <div className="relative">
          <input
            id={inputId}
            type={showKey ? "text" : "password"}
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Paste your API key here (e.g., AIza...)"
            autoComplete="off"
            spellCheck={false}
            className={cn(
              "w-full px-4 py-3 pr-24 rounded-lg border text-sm font-mono transition-colors",
              "bg-white dark:bg-gray-900",
              "placeholder:text-gray-400 dark:placeholder:text-gray-500",
              validationState === "valid" &&
                "border-green-300 dark:border-green-700 focus:ring-green-500 focus:border-green-500",
              validationState === "invalid" &&
                "border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500",
              validationState === "idle" &&
                "border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500",
              "focus:outline-none focus:ring-2",
            )}
          />
          {/* Show/Hide toggle */}
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label={showKey ? "Hide API key" : "Show API key"}
          >
            {showKey ? (
              <EyeOffIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Validation message */}
        {validationState === "invalid" && inputValue && (
          <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <ExclamationCircleIcon className="w-4 h-4" />
            This doesn't look like a valid API key format. Keys are usually 39+
            characters.
          </p>
        )}
        {validationState === "valid" && !isSaved && (
          <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <CheckCircleIcon className="w-4 h-4" />
            Looks good! Click "Save to Profile" to store it.
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!inputValue || validationState !== "valid" || isSaved}
          className={cn(
            "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
            inputValue && validationState === "valid" && !isSaved
              ? "bg-primary-500 text-white hover:bg-primary-600"
              : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed",
          )}
        >
          <SaveIcon className="w-4 h-4" />
          {isSaved ? "Saved to Profile" : "Save to Profile"}
        </button>

        {isSaved && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      {/* Saved indicator */}
      {isSaved && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-start gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                API Key Saved!
              </p>
              <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                Your key is stored securely in your browser. It will be
                remembered when you return to this page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* .env file generation */}
      {inputValue && validationState === "valid" && (
        <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              For your .env file
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              If you prefer to use a .env file, copy or download the content
              below:
            </p>
            <div className="relative">
              <pre className="p-3 bg-gray-900 dark:bg-black rounded-lg text-sm font-mono text-gray-100 overflow-x-auto">
                <code>
                  GOOGLE_GENAI_USE_VERTEXAI=FALSE{"\n"}
                  GOOGLE_API_KEY={showKey ? inputValue : "••••••••••••••••••••"}
                </code>
              </pre>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleCopyEnv}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <CopyIcon className="w-4 h-4" />
              Copy .env content
            </button>
            <button
              type="button"
              onClick={handleDownloadEnv}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <DownloadIcon className="w-4 h-4" />
              Download .env file
            </button>
          </div>
        </div>
      )}

      {/* Security note */}
      <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <div className="flex items-start gap-2">
          <LockIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Important Security Information
            </p>
            <ul className="text-xs text-amber-700 dark:text-amber-300 mt-1 space-y-1">
              <li>
                <strong>Never share</strong> your API key with anyone
              </li>
              <li>
                <strong>Don't commit</strong> .env files to version control
                (git)
              </li>
              <li>
                <strong>Browser storage:</strong> Your key is saved in
                localStorage, which is accessible to scripts on this page. For
                production use, consider server-side key management.
              </li>
              <li>
                <strong>Regenerate if exposed:</strong> If you suspect your key
                was compromised, regenerate it in Google AI Studio immediately.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
